import { Box, Typography } from "@mui/material";
import React, { useEffect } from "react";
import { Close, Search } from "@mui/icons-material";

import DefaultIcon from "assets/icons/default-product.svg";
import ProductSearchBox from "components/ProductSearchBox/ProductSearchBox";
import { darkGreyColor, darkRed, lightRed300 } from "theme";
import AnimatedLoader from "components/Loader/AnimatedLoader";
import QuickFilters from "components/QuickFilter/QuickFilter";
import useIndustryFilter from "hooks/useIndustryFilter";

import { useGetNAICSbyChatGPTMutation } from "../../redux/modules/config";

const isCardSelected =
  "w-full bg-[#F5FAFF] text-white  rounded-[8px] h-[6.625rem]";
const isCardNotSelected =
  "w-full hover:bg-[#F5FAFF] h-[6.625rem]  rounded-[8px]";

const ProductList = ({
  selectedCard,
  setSelectedCard,
  data,
  inputValue,
  setInputValue,
  setProductSelected,
  setApplicationProgress,
  setScreen,
  setApplicationData,
  setSnackbarData,
  setOpenSnackbar
}) => {
  const [filter, setFilter] = React.useState(null);

  const [getNAICS, { error, isLoading, data: codeData }] =
    useGetNAICSbyChatGPTMutation();

  const filteredIndustries = useIndustryFilter(filter, data);


  const handleSearch = () => {
    if (inputValue === "") {
      return;
    }
    const cleanedInput = inputValue.replace(/\s+/g, '');
    const isNumber = /^\d+(:\d+)*:?$/.test(cleanedInput);
    if (isNumber) {
      setProductSelected({ code: cleanedInput, naicsDescription: "" });
      setScreen("SelectCarrier");
      setSelectedCard({
        label: "",
        codes: []
      });
    } else {
      const payload = {data: { question: inputValue }};
      getNAICS(payload)
        .unwrap()
        .then((res) => {
          if (res) {
            const codes = res.result.map((item) => {
              return {
                code: item.naicsCode,
                naicsDescription: item.label
              };
            });
            cardSelect({
              label: "",
              codes
            });
            setSelectedCard({
              label: "",
              codes
            });
          } else {
            setOpenSnackbar(true);
            setSnackbarData({ message: "message", severity: "error" });
          }
        })
        .catch((err) => {
          setOpenSnackbar(true);
          setSnackbarData({ message: err?.data?.message, severity: "error" });
        });
    }
  };

  const cardSelect = (item) => {
    const [codeStr, description] = item.label.split(': ');

    setApplicationData(item);
    if (item.codes?.length > 1) {
      setSelectedCard(item);
    } else {
      setApplicationProgress("2");
      setProductSelected({
        code: codeStr,
        naicsDescription: description
      });
      setScreen("SelectCarrier");
    }
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Enter" && inputValue !== "") {
        event.preventDefault();
        handleSearch();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [inputValue]);

  if (isLoading) {
    return (
      <Box className="w-full h-[calc(100vh-100px)] flex justify-center items-center">
        <AnimatedLoader />
      </Box>
    );
  }

  return (
    <Box className="mt-3 p-6  bg-white rounded-[8px] w-full  cursor-pointer">
      <Box
        sx={{ border: "1px solid #C4CAD3" }}
        className="flex w-full h-full rounded-[8px] "
      >
        <input
          type="text"
          className="w-full ring-0 outline-none max-h-[48px]  py-3 pl-4 rounded-l-[8px] "
          placeholder="Type an industry or NAICS code, then press Enter."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
        />
        {selectedCard ? (
          <button
            className="h-auto w-[46px] bg-[#F6F8F9] rounded-[8px]"
            onClick={() => {
              setSelectedCard(null);
              setInputValue("");
              setProductSelected(null);
            }}
          >
            <Close />
          </button>
        ) : (
          <button
            onClick={() => handleSearch()}
            className="bg-[#3487F3]  flex items-center rounded-l-none text-white rounded-[4px] px-4"
          >
            <Search />

            <Typography
              sx={{
                fontSize: "1rem",
                color: "white",
                fontWeight: 600
              }}
            >
              Search
            </Typography>
          </button>
        )}
      </Box>
      { selectedCard == null && (
        <Box className="w-full">
          <QuickFilters filter={filter} setFilter={setFilter} />
        </Box>
      )}
      {error?.status === 400 ||
        (codeData?.result?.length === 0 && inputValue !== "" && (
          <Typography
            sx={{
              color: darkRed,
              bgcolor: lightRed300,
              p: 1,
              borderRadius: "8px",
              my: 2
            }}
          >
            {error?.data?.message || (
              <>
                We could not find any industries related to{" "}
                <strong>{inputValue}</strong>. Can you please try again with a
                different NAICS or a different keyword?
              </>
            )}
          </Typography>
        ))}

      {selectedCard && selectedCard?.codes?.length > 1 ? (
        <ProductSearchBox
          setApplicationProgress={setApplicationProgress}
          setProductSelected={setProductSelected}
          data={selectedCard?.codes}
          setScreen={setScreen}
        />
      ) : (
        <Box className="mt-6 grid grid-cols-5 gap-2 ">
          {filteredIndustries?.map((item, index) => (
            <Box
              key={index}
              className={`
                  ${item.label === selectedCard?.label ? isCardSelected : isCardNotSelected} flex flex-col justify-center h-[6.625rem] items-center
                `}
              sx={{
                border: "1px solid #E2E5E9",
                "&:hover": { border: "1px solid #3487F3" }
              }}
              onClick={() => cardSelect(item)}
            >
              <Box className={`bg-[#F6F7F8] p-2 rounded-[8px] mb-1`}>
                <img
                  src={item.activeIcon || DefaultIcon}
                  alt=""
                  className="w-8 h-8"
                />
              </Box>
              <Typography
                sx={{
                  color: darkGreyColor,
                  fontSize: "12px",
                  textTransform: "capitalize",
                  textAlign: "center"
                }}
              >
                {item.label?.slice(7, item.label.length)}
                </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ProductList;
